import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_VERTICAL = "/logos/solara-vertical.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#021d49] items-center justify-center p-12">
        <div className="text-center">
          <img src={LOGO_VERTICAL} alt="Solara Global" className="h-64 mx-auto mb-8" />
          <p className="text-white/70 text-lg max-w-md">
            Reset your password securely with Solara Global Capital Partners.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#fafaf9]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img src={LOGO_VERTICAL} alt="Solara Global" className="h-24 mx-auto" />
          </div>

          <div className="bg-white p-8 rounded shadow-sm border border-gray-200">
            {sent ? (
              <div className="text-center py-4">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="font-heading text-2xl font-bold text-[#021d49] mb-2">Check Your Email</h1>
                <p className="text-gray-500 mb-6">
                  If an account exists for <strong>{email}</strong>, we've sent a password reset link. Please check your inbox and spam folder.
                </p>
                <p className="text-gray-400 text-sm mb-6">The link will expire in 1 hour.</p>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-[#bd9a59] hover:underline font-medium"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            ) : (
              <>
                <h1 className="font-heading text-3xl font-bold text-[#021d49] mb-2">
                  Forgot Password?
                </h1>
                <p className="text-gray-500 mb-8">
                  Enter your email and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label className="form-label">Email Address</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="form-input"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#021d49] hover:bg-[#052b66] text-white py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="forgot-submit-btn"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Send Reset Link
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-gray-400 text-sm hover:text-[#bd9a59]"
                    data-testid="back-to-login"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
